<template>
  <section id="contact" class="section contact">
    <div class="container">
      <div class="section-head" v-reveal>
        <span class="eyebrow">04 / Contact</span>
        <h2 class="section-title">
          <span class="en">Contact</span>
          お問い合わせ
        </h2>
      </div>

      <p class="contact__lead" v-reveal="80">
        システム開発のご相談、生成AI導入の検討、既存サービスの運用保守まで。「まだ要件は固まっていない」という段階でも構いません。まずはお気軽にご相談ください。
      </p>

      <div class="contact__grid">
        <!-- Form -->
        <form class="form" novalidate @submit.prevent="submit" v-reveal="120">
          <div class="field">
            <label for="f-name">お名前<span class="req">必須</span></label>
            <input
              id="f-name"
              v-model.trim="form.name"
              type="text"
              name="name"
              autocomplete="name"
              :aria-invalid="!!errors.name"
              :aria-describedby="errors.name ? 'e-name' : null"
              @input="clearError('name')"
            />
            <p v-if="errors.name" id="e-name" class="field__error">{{ errors.name }}</p>
          </div>

          <div class="field">
            <label for="f-company">会社名<span class="opt">任意</span></label>
            <input
              id="f-company"
              v-model.trim="form.company"
              type="text"
              name="company"
              autocomplete="organization"
            />
          </div>

          <div class="field">
            <label for="f-email">メールアドレス<span class="req">必須</span></label>
            <input
              id="f-email"
              v-model.trim="form.email"
              type="email"
              name="email"
              autocomplete="email"
              inputmode="email"
              :aria-invalid="!!errors.email"
              :aria-describedby="errors.email ? 'e-email' : null"
              @input="clearError('email')"
            />
            <p v-if="errors.email" id="e-email" class="field__error">{{ errors.email }}</p>
          </div>

          <div class="field">
            <label for="f-phone">電話番号<span class="opt">任意</span></label>
            <input
              id="f-phone"
              v-model.trim="form.phone"
              type="tel"
              name="phone"
              autocomplete="tel"
              inputmode="tel"
            />
          </div>

          <div class="field">
            <label for="f-category">ご相談内容の種別</label>
            <div class="select">
              <select id="f-category" v-model="form.category" name="category">
                <option value="">選択してください</option>
                <option v-for="opt in categories" :key="opt" :value="opt">{{ opt }}</option>
              </select>
              <svg class="select__caret" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path d="M3 5l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </div>

          <div class="field">
            <label for="f-message">ご相談内容<span class="req">必須</span></label>
            <textarea
              id="f-message"
              v-model.trim="form.message"
              name="message"
              rows="6"
              :aria-invalid="!!errors.message"
              :aria-describedby="errors.message ? 'e-message' : null"
              @input="clearError('message')"
            ></textarea>
            <p v-if="errors.message" id="e-message" class="field__error">{{ errors.message }}</p>
          </div>

          <!-- Honeypot: hidden from users, catches bots -->
          <div class="hp" aria-hidden="true">
            <label for="f-website">Webサイト（入力しないでください）</label>
            <input
              id="f-website"
              v-model="form.website"
              type="text"
              name="website"
              tabindex="-1"
              autocomplete="off"
            />
          </div>

          <p
            v-if="status === 'success'"
            class="form__status form__status--ok"
            role="alert"
          >
            送信しました。内容を確認のうえ、折り返しご連絡いたします。
          </p>
          <p
            v-else-if="status === 'error'"
            class="form__status form__status--err"
            role="alert"
          >
            送信に失敗しました。お手数ですが contact@ash.qa まで直接ご連絡ください。
          </p>

          <button type="submit" class="btn btn--primary form__submit" :disabled="submitting">
            <template v-if="submitting">送信中…</template>
            <template v-else>
              この内容で送信する
              <svg class="btn__arrow" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </template>
          </button>
        </form>

        <!-- Contact details -->
        <aside class="details" v-reveal="200" aria-label="連絡先">
          <p class="details__note">
            フォームのほか、メール・お電話でも受け付けています。
          </p>
          <ul class="details__list">
            <li class="detail">
              <span class="detail__key">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.6" /><path d="M4 7l8 6 8-6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
                Email
              </span>
              <a href="mailto:contact@ash.qa" class="detail__val">contact@ash.qa</a>
            </li>
            <li class="detail">
              <span class="detail__key">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 4h3l2 5-2.5 1.5a11 11 0 0 0 5 5L16 12l5 2v3a2 2 0 0 1-2 2A15 15 0 0 1 4 6a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" /></svg>
                Phone
              </span>
              <a href="tel:07084038582" class="detail__val">(070) 8403-8582</a>
            </li>
            <li class="detail">
              <span class="detail__key">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 21s7-6.3 7-11a7 7 0 0 0-14 0c0 4.7 7 11 7 11z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" /><circle cx="12" cy="10" r="2.5" fill="none" stroke="currentColor" stroke-width="1.6" /></svg>
                所在地
              </span>
              <span class="detail__val">栃木県宇都宮市下戸祭1-3-11 904</span>
            </li>
          </ul>
        </aside>
      </div>
    </div>
  </section>
</template>

<script>
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'ContactSection',
  data() {
    return {
      categories: [
        '生成AI導入について',
        '受託開発について',
        'インフラ／運用保守について',
        '技術支援',
        'その他',
      ],
      form: {
        name: '',
        company: '',
        email: '',
        phone: '',
        category: '',
        message: '',
        website: '',
      },
      errors: {
        name: '',
        email: '',
        message: '',
      },
      submitting: false,
      status: 'idle',
    }
  },
  methods: {
    clearError(field) {
      if (this.errors[field]) this.errors[field] = ''
    },
    validate() {
      this.errors.name = this.form.name ? '' : 'お名前を入力してください。'
      if (!this.form.email) {
        this.errors.email = 'メールアドレスを入力してください。'
      } else if (!EMAIL_RE.test(this.form.email)) {
        this.errors.email = 'メールアドレスの形式が正しくありません。'
      } else {
        this.errors.email = ''
      }
      this.errors.message = this.form.message ? '' : 'ご相談内容を入力してください。'
      return !this.errors.name && !this.errors.email && !this.errors.message
    },
    async submit() {
      this.status = 'idle'
      if (!this.validate()) {
        this.$nextTick(() => {
          const el = this.$el.querySelector('[aria-invalid="true"]')
          if (el) el.focus()
        })
        return
      }
      this.submitting = true
      try {
        const body = new URLSearchParams()
        Object.keys(this.form).forEach((key) => body.append(key, this.form[key]))
        const res = await fetch('/contact.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: body.toString(),
        })
        const data = await res.json().catch(() => null)
        if (res.ok && data && data.ok) {
          this.status = 'success'
          this.resetForm()
        } else {
          this.status = 'error'
        }
      } catch (err) {
        this.status = 'error'
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      Object.keys(this.form).forEach((key) => {
        this.form[key] = ''
      })
    },
  },
}
</script>

<style scoped lang="scss">
.contact {
  border-top: 1px solid var(--line);
}

.contact__lead {
  max-width: 54ch;
  margin-top: clamp(28px, 4vw, 40px);
  font-size: clamp(1rem, 1.3vw, 1.12rem);
  line-height: 1.9;
  color: var(--ink-soft);
}

.contact__grid {
  display: grid;
  grid-template-columns: 1.35fr 0.65fr;
  gap: clamp(36px, 5vw, 72px);
  margin-top: clamp(44px, 6vw, 72px);
  align-items: start;
}

/* --- Form --- */
.form {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.field label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--ink);
}

.req,
.opt {
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  padding: 2px 7px;
  border-radius: 3px;
}

.req {
  color: var(--accent);
  background: var(--accent-mist);
}

.opt {
  color: var(--muted);
  background: var(--paper-sunk);
}

.field input,
.field textarea,
.select select {
  width: 100%;
  padding: 14px 16px;
  font-family: var(--font-body);
  font-size: 1rem;
  color: var(--ink);
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  transition: border-color 0.2s var(--ease), box-shadow 0.2s var(--ease);
  -webkit-appearance: none;
  appearance: none;
}

.field textarea {
  resize: vertical;
  min-height: 140px;
  line-height: 1.7;
}

.field input:focus,
.field textarea:focus,
.select select:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-mist);
}

.field input[aria-invalid="true"],
.field textarea[aria-invalid="true"] {
  border-color: #d13b3b;
  box-shadow: 0 0 0 3px rgba(209, 59, 59, 0.12);
}

.field__error {
  font-size: 0.82rem;
  color: #c62f2f;
}

/* Select */
.select {
  position: relative;
}

.select select {
  cursor: pointer;
  padding-right: 42px;
}

.select__caret {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--muted);
}

/* Honeypot */
.hp {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

/* Status + submit */
.form__status {
  padding: 15px 18px;
  border-radius: var(--radius);
  font-size: 0.92rem;
  line-height: 1.7;
}

.form__status--ok {
  color: #0f7a4d;
  background: rgba(16, 138, 87, 0.1);
  border: 1px solid rgba(16, 138, 87, 0.3);
}

.form__status--err {
  color: #c62f2f;
  background: rgba(209, 59, 59, 0.08);
  border: 1px solid rgba(209, 59, 59, 0.28);
}

.form__submit {
  align-self: flex-start;
  margin-top: 2px;
}

.form__submit:disabled {
  opacity: 0.65;
  cursor: progress;
  transform: none;
}

/* --- Details --- */
.details {
  position: sticky;
  top: calc(var(--header-h) + 28px);
  padding: clamp(26px, 3vw, 34px);
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
}

.details__note {
  font-size: 0.9rem;
  line-height: 1.8;
  color: var(--ink-soft);
  padding-bottom: 22px;
  border-bottom: 1px solid var(--line-soft);
}

.details__list {
  display: flex;
  flex-direction: column;
}

.detail {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 20px 0;
  border-bottom: 1px solid var(--line-soft);
}

.detail:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.detail__key {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}

.detail__val {
  font-size: 1rem;
  font-weight: 500;
  color: var(--ink);
  transition: color 0.2s var(--ease);
}

a.detail__val:hover {
  color: var(--accent);
}

@media (max-width: 860px) {
  .contact__grid {
    grid-template-columns: 1fr;
  }
  .details {
    position: static;
    order: -1;
  }
  .form__submit {
    align-self: stretch;
    justify-content: center;
  }
}
</style>
